/**
 * Script to manage custom behavior of select elements using HTML and JavaScript.
 * This script is executed when the DOM content is fully loaded.
 *
 * The purpose of this script is to hide the original select and make a fake select linked to the original.
 * This decision was made because select dropdown is not stylizable from scss files.
 * The emulated select is made up of multiple divs nested together with these classes:
 *
 * - "option-selected": class applied to the container of the original select and the emulated one.
 * - "select-selected": class applied to div that shows the option selected also when select is closed;
 * - "select-items": class applied to div container of "inner-item" (useful for stylizing);
 * - "inner-item" : class applied to the emulated dropdown;
 * - "same-as-selected" : class applied to the div selected by user as options inside "inner-item";
 * - "no-scrollbar": class applied with "inner-items" when there are only a few options and there is no need of scrollbar.
 * - "select-arrow-active": class applied with "option-selected" for showing the dropdown options when there was a
 *    click on the select.
 *
 * Inside the code there are conditions to prevent duplication of existing listeners and elements.
 *
 */
import {
  getCountryCodeISO3FromMetaTag,
  formatStringForAemPath,
  subscribeToResize,
  store,
  getTimeZoneEnable,
  convertUtcTimeRangeToLocal,
  getTimeSlotListPath,
  getCountryCodeFromMetaTag,
  formatTime,
  getUse24HourTime,
} from '@msc-b2c/shared-utils';

document.addEventListener('DOMContentLoaded', setupSelectListeners);

class CustomSelect {
  private readonly select: Element;
  private readonly selectButton: Element | null;
  private readonly selectedValue: Element | null;
  private optionsList: NodeListOf<Element>;
  private readonly dropdown: HTMLElement | null;
  private parentContainer: HTMLElement | null;
  private cleanup?: () => void;

  constructor(select: Element) {
    this.select = select;
    this.selectButton = select.querySelector('.select-button');
    this.selectedValue = select.querySelector('.selected-value');
    this.optionsList = select.querySelectorAll('.select-dropdown li');
    this.dropdown = select.querySelector('.select-dropdown');
    this.parentContainer = document
      .querySelector('.cmp-container-form-container')
      ?.querySelector('.cmp-container') as HTMLElement;
  }

  init() {
    this.bindEvents();
    const selectElement = this.select as HTMLSelectElement;
    if (this.select.classList.contains('custom-select')) {
      if (this.select.closest('.cmp-container__modal') != null) {
        //Set position fixed for when inside the modal
        this.dropdown!.style.position = 'fixed';
        //Detect scrolling to move the droplist with the select
        this.parentContainer?.addEventListener('scroll', () => this.updateDropdownPosition());
        //Detect window resizing to adjust the width of the droplist to match the select
        if (!this.cleanup) {
          this.cleanup = subscribeToResize(() => {
            if (!(this.select as HTMLElement).isConnected) {
              this.cleanup?.();
              this.cleanup = undefined;
              return;
            }
            this.updateDropdownWidth();
          });
        }
      }
      if (this.select.getAttribute('name') === 'customer.address.countryCode') {
        prepopulateCountrySelect(selectElement, false);
        this.enableKeyboardOpen(selectElement);
      }
    } else if (selectElement.name === 'customer.address.countryCode') {
      prepopulateCountrySelect(selectElement, true);
    } else {
      selectElement.disabled = !!selectElement
        .closest('div')
        ?.classList.contains('disabled-select');
    }
    this.enableKeyboardOpen(selectElement);
  }

  // allow keyboard to trigger events on the select
  private enableKeyboardOpen(selectElement: HTMLSelectElement) {
    selectElement.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        if (typeof selectElement.showPicker === 'function') {
          selectElement.showPicker();
        }
      }
    });
  }

  updateDropdownWidth() {
    if (this.select.closest('.cmp-container__modal') != null) {
      if (this.dropdown && this.selectButton) {
        this.dropdown.style.width = (this.selectButton as HTMLElement).offsetWidth + 'px';
      }
      this.updateDropdownPosition();
    }
  }

  updateDropdownPosition() {
    if (this.select.closest('.cmp-container__modal') != null) {
      if (this.dropdown && this.selectButton) {
        const selectButtonRect = this.selectButton.getBoundingClientRect();

        this.dropdown.style.top = `${selectButtonRect.bottom}px`;
        this.dropdown.style.left = `${selectButtonRect.left}px`;

        this.dropdown.style.width = `${selectButtonRect.width}px`;
      }
    }
  }

  //This function detects the other select elements in the same file and closes them if they are open
  handleOtherSelect() {
    const otherSelects = this.parentContainer?.querySelectorAll('.custom-select');
    otherSelects?.forEach((select) => {
      //if the select is active and its not the current one
      if (select.classList.contains('active') && select != this.select) {
        select.classList.toggle('active');
        select
          .querySelector('.select-button')
          ?.setAttribute(
            'aria-expanded',
            select.querySelector('.select-button')?.getAttribute('aria-expanded') === 'true'
              ? 'false'
              : 'true'
          );
      }
    });
  }

  // add click event to select button
  bindEvents() {
    const companyNameSelect = document.querySelector<HTMLSelectElement>(
      'select[name="existingMembership.company"]'
    );
    const membershipStatusSelect = document.querySelector<HTMLSelectElement>(
      'select[name="existingMembership.status"]'
    );
    if (companyNameSelect !== null && membershipStatusSelect !== null) {
      companyNameSelect.addEventListener('change', (event: Event) => {
        const target = event.target as HTMLSelectElement;
        const formattedValue = formatStringForAemPath(target.value);
        getParameterFromGenericList(
          formattedValue,
          'membership-status',
          false,
          'existingMembership.status'
        );
        document
          .querySelector(`select[name="existingMembership.status"]`)
          ?.closest('.options')
          ?.classList.add('disabled-select');
        document
          .querySelector(`select[name="existingMembership.programName"]`)
          ?.closest('.options')
          ?.classList.add('disabled-select');
      });
      membershipStatusSelect.addEventListener('change', (event: Event) => {
        const target = event.target as HTMLSelectElement;
        const selectedValueMembership = companyNameSelect.value + ' ' + target.value;
        const formattedValue = formatStringForAemPath(selectedValueMembership);

        getParameterFromGenericList(
          formattedValue,
          'loyalty-program-new',
          false,
          'existingMembership.programName'
        );
      });
    }
    this.selectButton?.addEventListener('click', (e: Event) => {
      this.toggleSelect(e);
    });

    this.optionsList.forEach((option) => {
      const handler = (e: MouseEvent | KeyboardEvent | Event) => {
        //Removing the selected class from all options
        this.optionsList.forEach((option) => {
          option.classList.remove('selected');
        });

        // Click Events
        if (
          e.type === 'click' &&
          'clientX' in e &&
          e.clientX !== 0 &&
          e.clientY !== 0 &&
          this.selectedValue
        ) {
          this.selectedValue.textContent = option.children[1].textContent;
        } else if ('key' in e && e.key === 'Enter' && this.selectedValue) {
          this.selectedValue.textContent = option.textContent;
        }

        option.classList.add('selected');
        validateSelectCustom(this.select as HTMLElement);
        this.select.classList.remove('active');
        const selectParent = this.select?.parentElement?.parentElement as HTMLElement;
        if (selectParent.classList.contains('cmp-select__lowerlabel-animation')) {
          this.select.classList.add('custom-label-upper-animation');
        }
        if (
          this.select.querySelector('.select-button')?.getAttribute('name') ===
          'existingMembership.company'
        ) {
          const nameList = formatStringForAemPath(option?.querySelector('input')?.value ?? '');
          getParameterFromGenericList(
            nameList,
            'membership-status',
            true,
            'existingMembership.status'
          );
          document
            .querySelector(`div.custom-select[name="existingMembership.status"]`)
            ?.closest('.options')
            ?.classList.add('disabled-select');
          const membershipStatusSpan = document
            .querySelector(`div.custom-select[name="existingMembership.status"]`)
            ?.querySelector('button')
            ?.querySelector('span');

          if (membershipStatusSpan) {
            membershipStatusSpan.textContent = '';
          }
          document
            .querySelector(`div.custom-select[name="existingMembership.programName"]`)
            ?.closest('.options')
            ?.classList.add('disabled-select');
          const loyaltyProgramSpan = document
            .querySelector(`div.custom-select[name="existingMembership.programName"]`)
            ?.querySelector('button')
            ?.querySelector('span');

          if (loyaltyProgramSpan) {
            loyaltyProgramSpan.textContent = '';
          }
        }
      };

      option.addEventListener('keyup', handler);
      option.addEventListener('click', handler);
    });
  }

  private toggleSelect(e: Event) {
    this.handleOtherSelect();
    e.preventDefault();
    if (this.selectButton?.getAttribute('name') === 'callback.selectedHour') {
      setTimeSelect(true, this.selectButton);
    }
    // add/remove active class on the container element
    this.select.classList.toggle('active');
    // update the aria-expanded attribute based on the current state
    this.selectButton?.setAttribute(
      'aria-expanded',
      this.selectButton.getAttribute('aria-expanded') === 'true' ? 'false' : 'true'
    );
    if (this.select.classList.contains('active')) {
      this.updateDropdownWidth();
    }
  }
}

export function prepopulateCountrySelect(selectElement: HTMLElement, native: boolean) {
  if (native) {
    const countrySelect = selectElement as HTMLSelectElement;
    if (countrySelect != null) {
      countrySelect.disabled = false;

      const countryCodeISO3 = getCountryCodeISO3FromMetaTag();
      const countryCodeISO2 = getCountryCodeFromMetaTag();
      const options = countrySelect.options;
      const enableToChooseAllItem: boolean =
        countrySelect.dataset['enabletochooseallitem'] === 'true';
      const sourceType = countrySelect.dataset['sourcetype'] ?? '';

      for (let i = 0; i < options.length; i++) {
        if (options[i].value === countryCodeISO3 || options[i].value === countryCodeISO2) {
          options[i].selected = true;
        } else {
          if (sourceType === 'list') {
            if (i == 0) {
              options[i].disabled = true;
            } else {
              options[i].disabled = !enableToChooseAllItem;
            }
          }
        }
      }
    }
  } else if (selectElement != null) {
    const countryCodeISO3 = getCountryCodeISO3FromMetaTag();
    const countryCodeISO2 = getCountryCodeFromMetaTag();
    const ulElement = selectElement.querySelector('ul') as HTMLUListElement;
    const buttonElement = selectElement.querySelector('button') as HTMLButtonElement;
    const span = buttonElement.querySelector('span') as HTMLSpanElement;
    const listItems = ulElement.querySelectorAll('li');
    const enableToChooseAllItem: boolean =
      selectElement.dataset['enabletochooseallitem'] === 'true';
    const sourceType = selectElement.dataset['sourcetype'] ?? '';

    for (let i = 0; i < listItems.length; i++) {
      const input = listItems[i].querySelector('input');
      if (input && (input.value === countryCodeISO3 || input.value === countryCodeISO2)) {
        const label = listItems[i].querySelector('label') as HTMLLabelElement;
        listItems[i].classList.add('selected');
        span.textContent = label.textContent;
      } else {
        if (sourceType === 'list') {
          if (!enableToChooseAllItem) {
            listItems[i].classList.add('disabled');
          }
        } else {
          const disabled: boolean = input?.dataset['itemdisabled'] === 'false';
          if (disabled) {
            listItems[i].classList.add('disabled');
          }
        }
      }
    }
  }
}

declare global {
  interface Window {
    timeSlotData: object;
  }
}

function parseSelectedDateParts(dateValue: string, fallbackDate: Date) {
  const parts = dateValue.split('-').map(Number);
  if (parts.length === 3 && parts.every((part) => !Number.isNaN(part))) {
    return { year: parts[0], month: parts[1], day: parts[2] };
  }

  return {
    year: fallbackDate.getFullYear(),
    month: fallbackDate.getMonth() + 1,
    day: fallbackDate.getDate(),
  };
}

function isSelectedDateToday(dateValue: string, fallbackDate: Date) {
  const selectedDateParts = parseSelectedDateParts(dateValue, fallbackDate);
  const selectedDate = new Date(
    selectedDateParts.year,
    selectedDateParts.month - 1,
    selectedDateParts.day
  );
  const today = new Date(
    fallbackDate.getFullYear(),
    fallbackDate.getMonth(),
    fallbackDate.getDate()
  );

  return selectedDate.getTime() === today.getTime();
}

function isWeekendDateValue(dateValue: string) {
  const parts = dateValue.split('-').map(Number);

  if (parts.length !== 3 || parts.some((part) => Number.isNaN(part))) {
    return false;
  }

  const selectedDate = new Date(parts[0], parts[1] - 1, parts[2]);

  return selectedDate.getDay() === 0 || selectedDate.getDay() === 6;
}

function shouldHideTimeSlot(slotValue: string, selectedDateValue: string, effectiveGmtNow: Date) {
  const [startTime, endTime] = slotValue.split('-');
  if (!startTime || !endTime) {
    return false;
  }

  const timeMatch = startTime.trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!timeMatch) {
    return false;
  }

  const fallbackDate = new Date();
  const { year, month, day } = parseSelectedDateParts(selectedDateValue, fallbackDate);
  // When timezone is enabled, slotValue is already in local time,
  // so we compare using local time directly instead of UTC
  const slotStart = getTimeZoneEnable()
    ? new Date(year, month - 1, day, Number(timeMatch[1]), Number(timeMatch[2]), 0, 0)
    : new Date(Date.UTC(year, month - 1, day, Number(timeMatch[1]), Number(timeMatch[2]), 0, 0));

  if (!isSelectedDateToday(selectedDateValue, fallbackDate)) {
    return false;
  }

  return slotStart <= effectiveGmtNow;
}

function setTimeSelect(customSelect: boolean, hourSelect: any) {
  let container;

  if (customSelect) {
    container = hourSelect.parentElement.parentElement.parentElement.parentElement;
  } else {
    container = hourSelect.parentElement.parentElement.parentElement;
  }
  const date = container.querySelector(
    'input[name="callback.selectedDay"]'
  ) as HTMLInputElement | null;
  const selectedDateValue = date?.value ?? '';
  const isTimeZoneEnabled = getTimeZoneEnable();

  if (isTimeZoneEnabled && isWeekendDateValue(selectedDateValue)) {
    hideAllTimeOption(customSelect, hourSelect, true);
    return;
  }

  const todayDate = new Date();

  const data = new Date(selectedDateValue);
  const today = new Date(todayDate.getFullYear(), todayDate.getMonth(), todayDate.getDate());
  const dateSelected = new Date(data.getFullYear(), data.getMonth(), data.getDate());

  if (dateSelected > today) {
    showAllTimeOption(customSelect, hourSelect);
  } else {
    hideAllTimeOption(customSelect, hourSelect, !selectedDateValue);
  }
}

function hideAllTimeOption(
  customSelect: boolean,
  hourSelect: HTMLSelectElement | null,
  forceRemove = false
) {
  const isTimeZoneEnabled = getTimeZoneEnable();
  const now = new Date();
  const maxsessiontime = hourSelect?.getAttribute('maxsessiontime');
  const leadTimeMinutes = maxsessiontime != null ? Number(maxsessiontime) : 0;
  const effectiveGmtNow = new Date(now.getTime() + leadTimeMinutes * 60000);
  const currentTime = now.getHours() + 1;

  if (customSelect) {
    const container = hourSelect?.parentElement?.parentElement?.parentElement?.parentElement;
    const selectedDateInput = container?.querySelector(
      'input[name="callback.selectedDay"]'
    ) as HTMLInputElement | null;
    const selectedDateValue = selectedDateInput?.value ?? '';
    const timeOption = container?.querySelectorAll("ul.select-dropdown input[type='radio']");
    let firstOption = true;
    timeOption?.forEach((option) => {
      const optionValue = (option as HTMLInputElement).value;
      const [startTime] = optionValue.split('-');
      const startHour = Number(startTime?.split(':')[0]);
      const shouldHideLegacy = !Number.isNaN(startHour) && startHour < currentTime;

      if (
        forceRemove ||
        (isTimeZoneEnabled
          ? shouldHideTimeSlot(optionValue, selectedDateValue, effectiveGmtNow)
          : shouldHideLegacy)
      ) {
        if (option.parentElement) {
          option.parentElement.style.display = 'none';
        }
      } else if (firstOption && option) {
        option?.parentElement?.classList.add('selected');
        const selectButton =
          option.parentElement?.parentElement?.parentElement?.querySelector('.select-button');
        const selectedValue = selectButton?.querySelector('.selected-value');

        if (selectedValue) {
          const label = option.parentElement?.querySelector('label');
          selectedValue.textContent = label?.textContent ?? (option as HTMLInputElement).value;
        }
        firstOption = false;
      }
    });
  } else if (hourSelect) {
    const container = hourSelect?.parentElement?.parentElement?.parentElement;
    const selectedDateInput = container?.querySelector(
      'input[name="callback.selectedDay"]'
    ) as HTMLInputElement | null;
    const selectedDateValue = selectedDateInput?.value ?? '';
    let firstVisibleValue: string | null = null;

    hourSelect.querySelectorAll('option').forEach((option: HTMLOptionElement) => {
      const optionValue = option.value;
      const [startTime] = optionValue.split('-');
      const startHour = Number(startTime?.split(':')[0]);
      const shouldHideLegacy = !Number.isNaN(startHour) && startHour < currentTime;
      if (
        optionValue?.includes('-') &&
        (forceRemove ||
          (isTimeZoneEnabled
            ? shouldHideTimeSlot(optionValue, selectedDateValue, effectiveGmtNow)
            : shouldHideLegacy))
      ) {
        if (isTimeZoneEnabled) {
          option.style.display = 'none';
        } else {
          option.remove();
        }
      } else {
        option.style.display = '';
        if (!firstVisibleValue && optionValue?.includes('-')) {
          firstVisibleValue = optionValue;
        }
      }
    });

    if (
      hourSelect.selectedOptions.length === 0 ||
      hourSelect.selectedOptions[0]?.style.display === 'none'
    ) {
      if (isTimeZoneEnabled && firstVisibleValue) {
        hourSelect.value = firstVisibleValue;
      }
    }
  }
}

function changeMembershipStatus(data: any, selectName: string) {
  const hiddenInput = document.querySelector<HTMLInputElement>(
    'input[name="existingMembership.programName"][type="hidden"]'
  );
  if (selectName === 'existingMembership.programName' && hiddenInput) {
    if (data) {
      const firstKey = Object.keys(data)[0];
      hiddenInput.value = data[firstKey]?.value ?? '';
    } else {
      hiddenInput.value = '';
    }
    return;
  }
  const selectDiv = document.querySelector(`div.custom-select[name="${selectName}"]`);
  if (!selectDiv) {
    return;
  }

  let ul = selectDiv.querySelector('ul.select-dropdown');

  if (!ul) {
    ul = document.createElement('ul');
    ul.classList.add('select-dropdown');
    ul.setAttribute('role', 'listbox');
    ul.id = `${selectName}`;

    const selectButton = selectDiv.querySelector('.select-button');
    if (selectButton) {
      selectButton.insertAdjacentElement('afterend', ul);
    } else {
      return;
    }
  }

  ul.innerHTML = '';

  if (data) {
    let firstElementCreated = true;
    for (const key in data) {
      const value = data[key].value;
      const li = document.createElement('li');
      li.setAttribute('role', 'option');

      if (firstElementCreated) {
        firstElementCreated = false;
      }

      const displayText = data[key].text ?? value;
      const input = document.createElement('input');
      input.type = 'radio';
      input.id = `${key}-${selectName}`;
      input.name = selectName;
      input.value = value;

      const label = document.createElement('label');
      label.setAttribute('for', input.id);
      label.textContent = displayText;

      li.appendChild(input);
      li.appendChild(label);
      ul.appendChild(li);

      li.addEventListener('click', () => {
        const allOptions = ul.querySelectorAll('li');
        allOptions.forEach((option) => option.classList.remove('selected'));
        li.classList.add('selected');

        const parentDiv = li.closest('.custom-select');
        const spanElement = parentDiv?.querySelector('.select-button .selected-value');
        if (spanElement) {
          spanElement.textContent = displayText;
        }
        const currentSelect = ul.closest('.custom-select');
        currentSelect?.classList.remove('active');
        if (selectName !== 'existingMembership.programName') {
          changeSelectMembership();
        }
        validateSelectCustom(currentSelect as HTMLElement);
      });
    }
  } else {
    const li = document.createElement('li');
    li.classList.add('no-options');
    ul.appendChild(li);
  }
}

function showAllTimeOption(customSelect: boolean, hourSelect: HTMLSelectElement) {
  if (customSelect) {
    const optionTime = document.querySelectorAll("ul.select-dropdown input[type='radio']");
    optionTime.forEach((option) => {
      if (option.parentElement) {
        option.parentElement.style.display = '';
      }
    });
  } else if (hourSelect) {
    hourSelect.querySelectorAll('option').forEach((option: HTMLOptionElement) => {
      option.style.display = '';
    });
  }
}

const hourSelects = document.querySelectorAll<HTMLSelectElement>(
  'select[name="callback.selectedHour"]'
);

hourSelects.forEach((hourSelect) => {
  hourSelect.addEventListener('focus', () => {
    getParameterFromGenericListTimeSlot(false, hourSelect.id, hourSelect);
  });
  getParameterFromGenericListTimeSlot(false, hourSelect.id, hourSelect);
});
const hourSelectsCustom = document.querySelectorAll<HTMLSelectElement>(
  'button[name="callback.selectedHour"]'
);
hourSelectsCustom.forEach((hourSelect) => {
  hourSelect.addEventListener('focus', () => {
    getParameterFromGenericListTimeSlot(true, hourSelect.id, hourSelect);
  });
  getParameterFromGenericListTimeSlot(true, hourSelect.id, hourSelect);
});

// Re-process time slots once the store config becomes available to apply timezone conversion
if (!getTimeZoneEnable()) {
  const unsubscribe = store.subscribe(() => {
    if (getTimeZoneEnable()) {
      unsubscribe();
      hourSelects.forEach((hourSelect) => {
        getParameterFromGenericListTimeSlot(false, hourSelect.id, hourSelect);
      });
      hourSelectsCustom.forEach((hourSelect) => {
        getParameterFromGenericListTimeSlot(true, hourSelect.id, hourSelect);
      });
    }
  });
}

document.querySelectorAll('.custom-select, select').forEach((node) => {
  new CustomSelect(node).init();
});

// Fetch the path of the timeslot list
// from context aware
// poll every 200ms until you see non-empty path
// if after 2secs you have empty string path propably is empty
// in config so we catch it as error and we select the default path
async function getGenericTimeSlotUrlFromContext(
  getTimeSlotListPath: () => string
): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const interval = setInterval(() => {
      const timeSlotListPath = getTimeSlotListPath();

      if (timeSlotListPath && timeSlotListPath !== '') {
        clearInterval(interval);
        clearTimeout(timeout);

        resolve(timeSlotListPath);
      }
    }, 200);

    const timeout = setTimeout(() => {
      clearInterval(interval);

      reject(new Error('Timeout: time-slot list path was not found.'));
    }, 8000);
  });
}

const DEFAULT_TIME_SLOT_LIST_URL =
  '/etc/acs-commons/lists/system-configurations/forms/time-slot-form/time-slot-form/jcr:content.list.json';

type TimeSlotListItem = { text?: string; value?: string };
type TimeSlotListData = Record<string, TimeSlotListItem>;

// we fetch the time-slot list path from context aware
// if anything wrong happens or path is empty
// we get the data from the default list
async function resolveTimeSlotListUrl(): Promise<string> {
  try {
    const timeSlotListPath = await getGenericTimeSlotUrlFromContext(getTimeSlotListPath);
    if (timeSlotListPath) {
      return `${timeSlotListPath}/jcr:content.list.json`;
    }
  } catch {
    // fall through to default list
  }
  return DEFAULT_TIME_SLOT_LIST_URL;
}

export async function fetchTimeSlotListData(): Promise<TimeSlotListData> {
  if (window['timeSlotData']) {
    return window['timeSlotData'] as TimeSlotListData;
  }

  const listUrl = await resolveTimeSlotListUrl();
  const response = await fetch(listUrl);
  if (!response.ok) {
    throw new Error('Response was not ok: ' + response.statusText);
  }
  const data = await response.json();
  window['timeSlotData'] = data;
  return data;
}

/**
 * Reads the start hour from the time-slot list in AEM.
 * Expected value format: "17:00-18:00" (UTC when timezone conversion is enabled).
 * When timezone conversion is enabled, slots are converted to local time and the
 * latest local start hour is returned so it can be compared with local getHours().
 */
export function getLastHourFromTimeSlotData(
  data: TimeSlotListData | null | undefined,
  fallback = 17
): number {
  if (!data) return fallback;

  const keys = Object.keys(data);
  if (keys.length === 0) return fallback;

  const useLocalTime = getTimeZoneEnable();

  if (useLocalTime) {
    let maxHour = -1;

    for (const key of keys) {
      const rawValue = data[key]?.value;
      if (!rawValue) continue;

      const slotValue = convertUtcTimeRangeToLocal(rawValue);
      const startTime = slotValue.split('-')[0]?.trim();
      const hour = parseInt(startTime?.split(':')[0] ?? '', 10);

      if (!Number.isNaN(hour) && hour > maxHour) {
        maxHour = hour;
      }
    }

    return maxHour >= 0 ? maxHour : fallback;
  }

  const lastValue = data[keys[keys.length - 1]]?.value;
  if (!lastValue) return fallback;

  const startTime = lastValue.split('-')[0]?.trim();
  const hour = parseInt(startTime?.split(':')[0] ?? '', 10);
  return Number.isNaN(hour) ? fallback : hour;
}

export async function getLastTimeSlotHour(fallback = 17): Promise<number> {
  try {
    const data = await fetchTimeSlotListData();
    return getLastHourFromTimeSlotData(data, fallback);
  } catch (error) {
    console.error('ERROR - Failed to retrieve time slot list for last hour:', error);
    return fallback;
  }
}

/**
 * Formats callback.selectedHour slot labels for display (option text / custom-select label).
 * The submitted value must stay in 24-hour format (e.g. "17:00-18:00") for the API;
 * only the visible label is converted to 12-hour AM/PM when use24HourTime is false.
 * Skipped when timezone conversion is disabled — slots are shown as authored in AEM.
 */
function formatCallbackHourDisplay(timeRange: string): string {
  if (!timeRange || getUse24HourTime() || !getTimeZoneEnable()) {
    // No AM/PM conversion: 24h config, or slots shown as-is without timezone localisation
    return timeRange;
  }

  const parts = timeRange.split('-');
  if (parts.length !== 2) {
    return formatTime(timeRange) || timeRange;
  }

  const start = formatTime(parts[0].trim());
  const end = formatTime(parts[1].trim());
  return `${start} - ${end}`;
}

export function getParameterFromGenericListTimeSlot(
  customSelect: boolean,
  selectName: string,
  hourSelect: HTMLSelectElement
) {
  fetchTimeSlotListData()
    .then((data) => {
      processTimeSlotData(data, customSelect, selectName, hourSelect);
    })
    .catch((error) => {
      console.error('ERROR - Failed to retrieve lists:', error);
    });
}

function processTimeSlotData(
  data: any,
  customSelect: boolean,
  selectName: string,
  hourSelect: HTMLSelectElement
) {
  const shouldConvertTimeSlots = getTimeZoneEnable();

  if (!customSelect) {
    const selectTarget = document.querySelector<HTMLSelectElement>(`select[id="${selectName}"]`);

    if (data) {
      if (selectTarget) {
        selectTarget.innerHTML = '';

        for (const key in data) {
          const gmtValue = data[key]?.value;
          const localValue =
            shouldConvertTimeSlots && gmtValue ? convertUtcTimeRangeToLocal(gmtValue) : gmtValue;

          createOption(
            selectTarget,
            localValue, // 24h value sent to the API
            formatCallbackHourDisplay(localValue), // display label only
            false,
            false,
            false
          );
        }
      } else {
        console.error(`Element with id ${selectName} not found.`);
      }
    }
    setTimeSelect(false, hourSelect);
  } else {
    const localData: any = {};
    for (const key in data) {
      const gmtValue = data[key]?.value;
      const localValue =
        shouldConvertTimeSlots && gmtValue ? convertUtcTimeRangeToLocal(gmtValue) : gmtValue;
      localData[key] = {
        ...data[key],
        value: localValue, // 24h value sent to the API (radio input value)
        text: formatCallbackHourDisplay(localValue), // display label only
      };
    }
    changeMembershipStatus(localData, hourSelect.name);
    setTimeSelect(true, hourSelect);
  }
}

function getParameterFromGenericList(
  nameList: string,
  nameFolderList: string,
  customSelect: boolean,
  selectName: string
) {
  fetch(
    `/etc/acs-commons/lists/system-configurations/forms/${nameFolderList}/${nameList}/jcr:content.list.json`
  )
    .then((response) => {
      if (!response.ok) {
        throw new Error(' response was not ok ' + response.statusText);
      }
      return response.json();
    })
    .then((data) => {
      const hiddenInput = document.querySelector<HTMLInputElement>(
        'input[name="existingMembership.programName"][type="hidden"]'
      );
      const selectNameElement = document.querySelector(`select[name="${selectName}"]`);

      if (selectName === 'existingMembership.programName' && hiddenInput) {
        if (data) {
          const firstKey = Object.keys(data)[0];
          hiddenInput.value = data[firstKey]?.value ?? '';
        } else {
          hiddenInput.value = '';
        }
      } else if (customSelect) {
        changeMembershipStatus(data, selectName);
        selectNameElement?.closest('.options')?.classList.remove('disabled-select');
        selectNameElement?.removeAttribute('disabled');
      } else {
        changeMembershipStatusSelect(data, selectName);
        selectNameElement?.closest('.options')?.classList.remove('disabled-select');
        selectNameElement?.removeAttribute('disabled');
        if (selectName === 'existingMembership.status') {
          clearLoyaltyProgramSelect('existingMembership.programName');
        }
      }
    })
    .catch((error) => {
      console.error('ERROR - Failed to retrieve lists:', error);
    });
}

function changeMembershipStatusSelect(data: any, select: string) {
  const selectTarget = document.querySelector<HTMLSelectElement>(`select[name="${select}"]`);
  if (data) {
    if (selectTarget) {
      selectTarget.innerHTML = '';
      const emptyOption = createEmptyOption(selectTarget);

      for (const key in data) {
        createOption(selectTarget, data[key]?.value, data[key]?.text, false, false, false);
      }

      selectTarget.addEventListener('change', () => {
        if (selectTarget.value !== '') {
          emptyOption.hidden = true;
        }
      });
    }
  }
}

function createEmptyOption(selectTarget: HTMLSelectElement) {
  const optionText = selectTarget.getAttribute('placeholder') ?? '';
  return createOption(selectTarget, '', optionText, true, true, false);
}

function createOption(
  selectTarget: HTMLSelectElement,
  value: string,
  text: string,
  selected: boolean,
  disabled: boolean,
  hidden: boolean
) {
  const option = document.createElement('option');
  option.value = value;
  option.text = text;
  option.hidden = hidden;
  option.disabled = disabled;
  option.selected = selected;
  selectTarget.appendChild(option);

  return option;
}

function clearLoyaltyProgramSelect(select: string) {
  const selectTarget = document.querySelector<HTMLSelectElement>(`select[name="${select}"]`);
  if (selectTarget) {
    createEmptyOption(selectTarget);
  }
  const hiddenInput = document.querySelector<HTMLInputElement>(
    'input[name="existingMembership.programName"][type="hidden"]'
  );
  if (hiddenInput) {
    hiddenInput.value = '';
  }
}

function changeSelectMembership() {
  const optionListCompany = document
    .querySelector('button.select-button[name="existingMembership.company"]')
    ?.parentElement?.querySelectorAll('li');
  const optionList = document
    .querySelector('button.select-button[name="existingMembership.status"]')
    ?.parentElement?.querySelectorAll('li');
  let valueCompanyName = '';

  const selectedOption = optionList
    ? Array.from(optionList).find((li) => li.classList.contains('selected'))
    : null;
  const selectOptionCompany = optionListCompany
    ? Array.from(optionListCompany).find((li) => li.classList.contains('selected'))
    : null;
  if (selectOptionCompany) {
    const inputElement = selectOptionCompany.querySelector('input');
    if (inputElement) {
      valueCompanyName = inputElement.value;
    }
  }
  if (selectedOption) {
    const inputElement = (selectedOption as HTMLElement).querySelector('input');
    if (inputElement) {
      const inputValue = inputElement.value;
      const nameList = formatStringForAemPath(valueCompanyName + ' ' + inputValue);

      getParameterFromGenericList(
        nameList,
        'loyalty-program-new',
        true,
        'existingMembership.programName'
      );
    }
  }
}

export function validateSelect(input: Event | HTMLSelectElement): boolean {
  const select = input instanceof Event ? (input.currentTarget as HTMLSelectElement) : input;

  if (!select.getAttribute('data-validation')) {
    return true;
  }

  const selectToError = select.parentElement?.parentElement;

  if (selectToError?.classList.contains('disabled-element-input-select')) {
    return true;
  } else if (!select.value) {
    selectToError?.classList.add('error-select');
    selectToError
      ?.querySelector('.vr-mandatory-field')
      ?.classList.remove('input-hidden-error-messagge-vr');
    return false;
  } else {
    selectToError?.classList.remove('error-select');
    selectToError
      ?.querySelector('.vr-mandatory-field')
      ?.classList.add('input-hidden-error-messagge-vr');
    return true;
  }
}

export function validateSelectCustom(select: HTMLElement): boolean {
  if (!select.getAttribute('data-validation')) {
    return true;
  }
  const selectedLi = select.querySelector('li.selected');
  const selectContainer = select.parentElement?.parentElement;

  if (selectContainer?.classList.contains('disabled-element-input-select')) {
    return true;
  } else if (!selectedLi) {
    selectContainer?.classList.add('error-select');
    selectContainer
      ?.querySelector('.vr-mandatory-field')
      ?.classList.remove('input-hidden-error-messagge-vr');
    return false;
  } else {
    selectContainer?.classList.remove('error-select');
    selectContainer
      ?.querySelector('.vr-mandatory-field')
      ?.classList.add('input-hidden-error-messagge-vr');
    return true;
  }
}

let isAnyButtonClicked = false;

function setupSelectListeners() {
  const selects = document.querySelectorAll('select');
  selects.forEach((select) => {
    select.addEventListener('blur', validateSelect);
    select.addEventListener('select', validateSelect);
    select.addEventListener('focusout', validateSelect);
  });

  const customSelectButtons: NodeListOf<HTMLElement> = document.querySelectorAll('.custom-select');
  customSelectButtons.forEach((button: HTMLElement) => {
    button.addEventListener('click', handleCustomSelectButtonClick);
  });
}

function handleCustomSelectButtonClick(event: MouseEvent): void {
  isAnyButtonClicked = true;
  closeAllSelectsOpenCustom(event);
  document.addEventListener('click', handleGlobalClick);
}

function handleGlobalClick(event: MouseEvent): void {
  if (isAnyButtonClicked) {
    closeAllSelectsOpenCustom(event);
  }
}

function closeAllSelectsOpenCustom(event: Event) {
  const selects = document.querySelectorAll('.custom-select');
  const target = event.target as HTMLElement;
  if (target.closest('.custom-select')) {
    return;
  } else {
    selects.forEach((select) => {
      if (select.classList.contains('active')) {
        validateSelectCustom(select as HTMLElement);
        select.classList.remove('active');
        select
          .querySelector('.select-button')
          ?.setAttribute(
            'aria-expanded',
            select.querySelector('.select-button')?.getAttribute('aria-expanded') === 'true'
              ? 'false'
              : 'true'
          );
      }
    });
  }
}

export function validateRadioGroup(formContainer: HTMLFormElement): boolean {
  const radioButtonGroups = Array.from(formContainer.querySelectorAll('.cmp-form-options--radio'));
  let isFormValid = true;
  radioButtonGroups.forEach((radioGroup) => {
    const radiogroups = radioGroup.querySelectorAll('input[type="radio"]');
    const isAnyChecked = Array.from(radiogroups).some(
      (radiogroup) => (radiogroup as HTMLInputElement).checked
    );
    const errorMessage = radioGroup.parentElement?.querySelector('.vr-mandatory-field');

    if (errorMessage && !isAnyChecked) {
      errorMessage.classList.remove('input-hidden-error-messagge-vr');
      errorMessage.classList.add('visible');
      isFormValid = false;
    }
  });
  return isFormValid;
}

export function validateCheckboxGroup(formContainer: HTMLFormElement): boolean {
  const checkboxGroups = Array.from(formContainer.querySelectorAll('.cmp-form-options--checkbox'));
  let isFormValid = true;
  checkboxGroups.forEach((checkboxGroup) => {
    const checkboxes = checkboxGroup.querySelectorAll('input[type="checkbox"]');
    const isAnyChecked = Array.from(checkboxes).some(
      (checkbox) => (checkbox as HTMLInputElement).checked
    );
    const errorMessage = checkboxGroup.parentElement?.querySelector('.vr-mandatory-field');

    if (errorMessage && !isAnyChecked) {
      errorMessage.classList.remove('input-hidden-error-messagge-vr');
      errorMessage.classList.add('visible');
      isFormValid = false;
    }
  });
  return isFormValid;
}

const checkboxElements = document.querySelectorAll<HTMLInputElement>('input[type="checkbox"]');
checkboxElements.forEach((checkbox) => {
  checkbox.addEventListener('change', (event) => {
    const target = event.target as HTMLInputElement;
    const errorMessage = target
      .closest('.options')
      ?.querySelector('.vr-mandatory-field') as HTMLElement | null;
    if (errorMessage) {
      errorMessage.classList.toggle('input-hidden-error-messagge-vr', target.checked);
      errorMessage.classList.toggle('visible', !target.checked);
    }
  });
});

const radioElements = document.querySelectorAll<HTMLInputElement>('input[type="radio"]');
radioElements.forEach((radio) => {
  radio.addEventListener('change', (event) => {
    const target = event.target as HTMLInputElement;
    const errorMessage = target
      .closest('.options')
      ?.querySelector('.vr-mandatory-field:not(.accessibility-hidden)') as HTMLElement | null;
    if (errorMessage) {
      errorMessage.classList.toggle('input-hidden-error-messagge-vr', target.checked);
      errorMessage.classList.toggle('visible', !target.checked);
    }
  });
});
